import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import Margin from "../Margin";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Loader from "Components/Loader/loader";
import { useFetchGetConditional, useFetchGetPagination } from "Services/api";
import { DateTime } from "luxon";
import { isNotEmptyArray } from "Services/functions";

/**
 * @typedef {Object.<string, Object>} OfferTableState
 */

/**
 * @typedef {Object.<number, Object>} StatsTable
 */
/**
 * Composant 'OffersTable'.
 *
 * Ce composant affiche un tableau des offres avec différentes options de tri et d'exportation.
 *
 * Actuellement, le tableau ne s'affiche pas sur si l'utilisateur est sur un téléphone.
 *
 * @param {Object} props - Les propriétés passées au composant.
 * @param {Object} props.auth - Les données d'authentification. (depuis le store redux)
 * @param {Object} props.states - Les données des états des offres. (depuis le store redux)
 * @param {Object} props.secondaryTables - Les données des tables secondaires. (depuis le store redux)
 *
 * @returns {JSX.Element} Le tableau des offres avec les options de tri et d'exportation.
 */
const OffersTable = (props) => {
  const dt = useRef(null);

  //? Filtres du tableau
  const [dateRange, setDateRange] = useState([
    DateTime.local()
      .startOf("month")
      .minus({ months: 6 })
      .plus({ days: 1 })
      .toJSDate(),
    DateTime.local().endOf("month").minus({ months: 1 }).toJSDate(),
  ]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDiffusers, setSelectedDiffusers] = useState(null);
  const [selectedTransmitter, setSelectedTransmitter] = useState(null);

  const [dataUrl, setDataUrl] = useState(undefined);

  const dataQuery = useFetchGetPagination(dataUrl, props.auth.token);

  const diffusers = useFetchGetConditional(
    "/diffusers",
    props.auth.token,
    props.secondaryTables.diffusersData
  );

  const [diffusersOptions, setDiffusersOptions] = useState([]);

  const transmitters = useFetchGetConditional(
    "/transmitters",
    props.auth.token,
    props.secondaryTables.transmittersData
  );
  const [transmittersOptions, setTransmitterOptions] = useState([]);

  const offersCategories = useFetchGetConditional(
    "/offer_categories",
    props.auth.token,
    props.secondaryTables.offerCategoriesData
  );

  /**
   * Fonction qui gère les filtres du tableau des offres
   *
   * @param {URL} url - L'url de l'API
   * @returns {URL} - L'url de l'API avec les filtres
   */
  const handleFilters = (url) => {
    if (selectedCategory && selectedCategory.length > 0) {
      selectedCategory.forEach((cat) => {
        url.searchParams.append("categories[]", cat.id);
      });
    }
    if (selectedDiffusers && selectedDiffusers.length > 0) {
      selectedDiffusers.forEach((diff) => {
        url.searchParams.append("diffusers[]", diff.id);
      });
    }
    if (selectedTransmitter && selectedTransmitter.length > 0) {
      selectedTransmitter.forEach((tr) => {
        url.searchParams.append("transmitters[]", tr.id);
      });
    }
    if (dateRange) {
      url.searchParams.append(
        "postedAt[after]",
        dateRange[0].toISOString().slice(0, 10)
      );
      url.searchParams.append(
        "postedAt[before]",
        dateRange[1].toISOString().slice(0, 10)
      );
    }
    return url;
  };

  /**
   * Fonction qui prépare l'url pour le call API
   *
   * - Crée l'url pointant vers l'API
   * - Ajoute les filtres
   * - Set l'url finalisée dans le state
   */
  const prepareUrl = async () => {
    let url = new URL(
      `${process.env.REACT_APP_BASE_URL_API}/offers/stats/informations`
    );
    url = handleFilters(url);

    setDataUrl(url);
  };

  /**
   * Une fois que la liste des diffuseurs est chargée,
   * on trie les diffuseurs par ordre alphabétique
   * et on l'ajoute dans le state
   */
  useEffect(() => {
    if (diffusers.loaded)
      setDiffusersOptions(
        diffusers.data.sort((a, b) => a.name.localeCompare(b.name))
      );
  }, [diffusers.loaded]);

  /**
   * UseEffect appelé une fois que la liste des transmetteurs est chargée
   */
  useEffect(() => {
    //? On groupe les transmetteurs renvoyés par l'API derrière
    //? des noms plus explicites, affichés dans le tableau
    if (isNotEmptyArray(props.secondaryTables.transmittersData)) {
      setTransmitterOptions([
        {
          label: "Portail entreprise",
          id:
            props.secondaryTables.transmittersData.find(
              (tr) => tr.name === "Recruteur"
            ).id || null,
        },
        {
          label: "Site établissement",
          id:
            //? Ajoute tous les ids correspondant aux transmetteurs
            //? dans une string au format "id1,id2,id3"
            props.secondaryTables.transmittersData
              .filter(
                (tr) =>
                  tr.name == "Alumni" ||
                  tr.name == "Admin" ||
                  tr.name == "Etudiant" ||
                  tr.name == "Personnel"
              )
              .map((tr) => tr.id)
              .join(",") || null,
        },
        {
          label: "Diffuseur",
          id:
            props.secondaryTables.transmittersData.find(
              (tr) => tr.name === "Diffuseur"
            ).id || null,
        },
      ]);
    }
  }, [props.secondaryTables.transmittersData]);

  /**
   * A chaque changement de filtre, on prépare l'url
   * pour re-effectué un call API
   *
   * - Changement de catégorie
   * - Changement de diffuseur
   * - Changement de transmetteur
   * - Changement de date
   */
  useEffect(async () => {
    if (
      dateRange[0] instanceof Date &&
      !isNaN(dateRange[0]) &&
      dateRange[1] instanceof Date &&
      !isNaN(dateRange[1])
    )
      await prepareUrl();
  }, [selectedCategory, selectedDiffusers, selectedTransmitter, dateRange]);

  const cols = [
    { field: "state", header: "Statut de l'offre" },
    { field: "total", header: "Nombre d'offres" },
    { field: "viewsNumber", header: "Nombre de vues" },
    {
      field: "applyButtonsClick",
      header: "Nombre de clic bouton 'Postuler`",
    },
    { field: "applicationCount", header: "Nombre de candidatures" },
    { field: "diffuseurCount", header: "Diffuseur" },
    { field: "universityCount", header: "Site établissement" },
    { field: "recruteurCount", header: "Portail entreprise" },
  ];

  /**
   * Récupère les colonnes du tableau pour l'exportation
   * au format CSV, XLS et PDF
   */
  const exportColumns = cols.map((col) => ({
    title: col.header,
    dataKey: col.field,
  }));

  /**
   * Exporte le tableau des offres au format .csv
   */
  const exportCSV = () => {
    dt.current.exportCSV(dataQuery?.data);
  };

  /**
   * Exporte le tableau des offres au format .pdf
   *
   * - Utilise la librairie jspdf
   */
  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default(0, 0);
        doc.autoTable(exportColumns, dataQuery?.data);
        doc.save("statutOffres.pdf");
      });
    });
  };

  /**
   * Exporte le tableau des offres au format .xlsx
   *
   * - Utilise la librairie xlsx
   */
  const exportTableExcel = () => {
    import("xlsx").then((xlsx) => {
      //For headers, use header from cols variable by comparing with field
      const excelData = [...dataQuery.data];
      const headers = cols.map((col) => col.header);
      const tableData = [headers];
      excelData.forEach((e) => {
        const rowData = [];
        headers.forEach((col) => {
          const value = e[cols.find((c) => c.header === col).field];
          rowData.push(value);
        });
        tableData.push(rowData);
      });
      const workbook = xlsx.utils.book_new();
      const worksheet = xlsx.utils.aoa_to_sheet(tableData);
      xlsx.utils.book_append_sheet(workbook, worksheet, "Statut des offres");
      xlsx.writeFile(workbook, "statut-offres.xlsx");
    });
    return;
  };

  /**
   * Header du tableau
   *
   * @returns {JSX.Element} - Les boutons d'exportation sous les
   * différents formats (CSV, XLS, PDF)
   */
  const header = (
    <div className="flex align-items-center export-buttons stats_container__table-tooltip">
      <Button
        type="button"
        icon="pi pi-file"
        onClick={() => exportCSV(false)}
        className="mr-2 csv"
        data-pr-tooltip="CSV"
      />
      <Button
        type="button"
        icon="pi pi-file-excel"
        onClick={exportTableExcel}
        className="p-button-success mr-2 xl"
        data-pr-tooltip="XLS"
      />
      <Button
        type="button"
        icon="pi pi-file-pdf"
        onClick={exportPdf}
        className="p-button-warning mr-2 pdf"
        data-pr-tooltip="PDF"
      />
    </div>
  );

  return (
    <div>
      <div className="stats_container__table">
        <div className="stats_container__table__forms">
          <br />
          {offersCategories?.loaded &&
          transmitters?.loaded &&
          diffusers?.loaded ? (
            <>
              <div className="stats_container__table__forms-dual">
                <Calendar
                  value={dateRange}
                  className="stats-calendar"
                  onChange={(e) => {
                    if (e.value[0] && e.value[1]) {
                      let beforeDate = DateTime.fromJSDate(e.value[0]);
                      let afterDate = DateTime.fromJSDate(e.value[1]).plus({
                        months: 1,
                      });
                      setDateRange([
                        beforeDate.toJSDate(),
                        afterDate.toJSDate(),
                      ]);
                    } else {
                      setDateRange(e.value);
                    }
                  }}
                  showIcon
                  view="month"
                  dateFormat="mm/yy"
                  selectionMode="range"
                  maxDate={DateTime.local().minus({ months: 1 }).toJSDate()}
                />

                <MultiSelect
                  appendTo={document.getElementById("app")}
                  id="offersTypes"
                  value={selectedCategory}
                  options={offersCategories?.data?.sort((a, b) =>
                    a.label.localeCompare(b.label)
                  )}
                  onChange={(e) => setSelectedCategory(e.value)}
                  optionLabel="label"
                  placeholder="Trier par catégorie d'offre"
                />
              </div>
              <Margin position="bottom" size="s" />

              <div className="stats_container__table__forms-dual">
                <MultiSelect
                  appendTo={document.getElementById("app")}
                  id="diffusers"
                  value={selectedDiffusers}
                  options={diffusersOptions}
                  onChange={(e) => setSelectedDiffusers(e.value)}
                  optionLabel="name"
                  placeholder="Trier par diffuseur"
                />
                <MultiSelect
                  appendTo={document.getElementById("app")}
                  id="transmitters"
                  value={selectedTransmitter}
                  options={
                    typeof transmittersOptions !== "undefined" &&
                    transmittersOptions?.sort(
                      (a, b) => a?.label?.localeCompare(b?.label) || []
                    )
                  }
                  onChange={(e) => setSelectedTransmitter(e.value)}
                  optionLabel="label"
                  placeholder="Trier par provenance"
                />
              </div>
            </>
          ) : (
            <Loader />
          )}
        </div>
        <h5 className="stats_container__table-title">Statut des offres</h5>
        <Margin position="bottom" size="xxs" />
        <Tooltip target=".export-buttons>button" position="bottom" />
        {dataQuery.loaded ? (
          <DataTable
            id="offers-table"
            value={dataQuery?.data}
            responsiveLayout="scroll"
            dataKey="id"
            header={header}
            ref={dt}
          >
            {cols.map((col, index) => (
              <Column key={index} field={col.field} header={col.header} />
            ))}
          </DataTable>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};

OffersTable.propTypes = {
  auth: PropTypes.object,
  states: PropTypes.object,
  secondaryTables: PropTypes.object,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  states: state.states,
  secondaryTables: state.secondaryTables,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(OffersTable);
